/** @typedef {import('headlight-configurable-application').Pict} Pict */

const PictSectionInputExtension = require('pict-section-form').PictInputExtensionProvider;

class StationInputHandler extends PictSectionInputExtension
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {import('fable') & { HeadlightStationing: import('../../../../service/headlight-stationing-service/types/Headlight-Stationing') }} */
		this.fable;
		/** @type {Pict} */
		this.pict;
	}

	getStationHiddenInputHTMLID(pInputHTMLID)
	{
		return `#${pInputHTMLID}`;
	}

	getStationDisplayInputHTMLID(pInputHTMLID)
	{
		return `#STATION-INPUT-FOR-${pInputHTMLID}`;
	}

	getTabularStationHiddenInputHTMLID(pInputHTMLID, pRowIndex)
	{
		return `#${pInputHTMLID}-${pRowIndex}`;
	}

	getTabularStationDisplayInputHTMLID(pInputHTMLID, pRowIndex)
	{
		return `#STATION-TABULAR-FOR-${pInputHTMLID}-${pRowIndex}`;
	}

	getValueRepresentations(pValue)
	{
		let tmpNumericValue;
		let tmpContent;
		if (isNaN(pValue))
		{
			tmpContent = pValue;
			tmpNumericValue = this.fable.HeadlightStationing.fromStationValue(pValue);
		}
		else
		{
			tmpNumericValue = pValue;
			tmpContent = this.fable.HeadlightStationing.toStationValue(tmpNumericValue);
		}
		return [ tmpNumericValue, tmpContent ];
	}

	/**
	 * Marshals data to the form for the given input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to be marshaled.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pTransactionGUID - The transaction GUID.
	 * @return {boolean}
	 */
	onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		if (!pValue)
		{
			return super.onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
		}
		const [ tmpNumericValue, tmpStringValue ] = this.getValueRepresentations(pValue);
		if (isNaN(tmpNumericValue))
		{
			return super.onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
		}
		this.pict.ContentAssignment.assignContent(this.getStationDisplayInputHTMLID(pInput.Macro.RawHTMLID), tmpStringValue);
		this.pict.ContentAssignment.assignContent(this.getStationHiddenInputHTMLID(pInput.Macro.RawHTMLID), tmpNumericValue);

		return super.onDataMarshalToForm(pView, pGroup, pRow, pInput, tmpNumericValue, pHTMLSelector, pTransactionGUID);
	}

	/**
	 * Fires when data is marshaled to the form for this input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group definition object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to marshal.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {number} pRowIndex - The index of the input in the row columns.
	 * @param {string} pTransactionGUID - The transaction GUID.
	 * @return {boolean} - Returns true if the data was successfully marshaled to the form.
	 */
	onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		if (!pValue)
		{
			return super.onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
		}
		const [ tmpNumericValue, tmpStringValue ] = this.getValueRepresentations(pValue);
		if (isNaN(tmpNumericValue))
		{
			return super.onDataMarshalToFormTabular(pView, pGroup, pInput, tmpNumericValue, pHTMLSelector, pRowIndex, pTransactionGUID);
		}
		this.pict.ContentAssignment.assignContent(this.getTabularStationDisplayInputHTMLID(pInput.Macro.RawHTMLID, pRowIndex), tmpStringValue);
		this.pict.ContentAssignment.assignContent(this.getTabularStationHiddenInputHTMLID(pInput.Macro.RawHTMLID, pRowIndex), tmpNumericValue);

		return super.onDataMarshalToFormTabular(pView, pGroup, pInput, tmpNumericValue, pHTMLSelector, pRowIndex, pTransactionGUID);
	}

	/**
	 * Handles the data request event for a select input in the PictProviderInputSelect class.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value object.
	 * @param {string} pHTMLSelector - The HTML selector object.
	 * @return {any} - The result of the onDataRequest method.
	 */
	onDataRequest(pView, pInput, pValue, pHTMLSelector)
	{
		const tmpValue = this.pict.ContentAssignment.readContent(this.getStationDisplayInputHTMLID(pInput.Macro.RawHTMLID));
		const [ tmpNumericValue, tmpStringValue ] = this.getValueRepresentations(tmpValue);
		if (isNaN(tmpNumericValue))
		{
			return super.onDataRequest(pView, pInput, tmpValue, pHTMLSelector);
		}
		this.pict.ContentAssignment.assignContent(this.getStationDisplayInputHTMLID(pInput.Macro.RawHTMLID), tmpStringValue);
		this.pict.ContentAssignment.assignContent(this.getStationHiddenInputHTMLID(pInput.Macro.RawHTMLID), tmpNumericValue);
		pView.dataChanged(pInput.Hash);
		return super.onDataRequest(pView, pInput, pValue, pHTMLSelector);
	}

	/**
	 * Handles the data request event for a tabular input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value object.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {number} pRowIndex - The row index.
	 * @return {any} - The result of the data request.
	 */
	onDataRequestTabular(pView, pInput, pValue, pHTMLSelector, pRowIndex)
	{
		const tmpValue = this.pict.ContentAssignment.readContent(this.getTabularStationDisplayInputHTMLID(pInput.Macro.RawHTMLID, pRowIndex));
		const [ tmpNumericValue, tmpStringValue ] = this.getValueRepresentations(tmpValue);
		if (isNaN(tmpNumericValue))
		{
			return super.onDataRequestTabular(pView, pInput, tmpValue, pHTMLSelector, pRowIndex);
		}
		this.pict.ContentAssignment.assignContent(this.getTabularStationDisplayInputHTMLID(pInput.Macro.RawHTMLID, pRowIndex), tmpStringValue);
		this.pict.ContentAssignment.assignContent(this.getTabularStationHiddenInputHTMLID(pInput.Macro.RawHTMLID, pRowIndex), tmpNumericValue);

		pView.dataChangedTabular(pInput.PictForm.GroupIndex, pInput.PictForm.InputIndex, pRowIndex);
		return super.onDataRequestTabular(pView, pInput, pValue, pHTMLSelector, pRowIndex);
	}
}

module.exports = StationInputHandler;
